import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useWallet } from '../../contexts/WalletContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getClassNames } from '../../styles/theme';
import ThemeToggle from '../ui/ThemeToggle';
import { 
  BookOpen, 
  Hammer, 
  Compass, 
  Wallet, 
  Menu,
  X
} from 'lucide-react';
import logoSimple from '../../assets/logos/LL (simple).png';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { isConnected, connectWallet, disconnectWallet, address } = useWallet();
  const { theme } = useTheme();
  const classNames = getClassNames(theme);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle scroll for transparent nav effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navLinks = [
    { path: '/learn', label: 'Learn', icon: BookOpen },
    { path: '/build', label: 'Build', icon: Hammer },
    { path: '/explore', label: 'Explore', icon: Compass },
    { path: '/wallet', label: 'Wallet', icon: Wallet },
  ];

  const navLinkClass = (path: string) => `
    ${classNames.nav.link}
    ${isActive(path) 
      ? classNames.nav.linkActive
      : classNames.nav.linkInactive
    }
  `;

  return (
    <div className={`min-h-screen ${classNames.background}`}>
      {/* Sticky Navigation */}
      <nav className={`
        ${classNames.nav.container}
        ${isScrolled 
          ? classNames.nav.scrolled
          : classNames.nav.transparent
        }
      `}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Bar - Logo and User Menu */}
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <Link to="/" className="flex flex-col items-start cursor-pointer hover:opacity-80 transition-opacity">
              <img src={logoSimple} alt="LegacyLayer" className="w-12 h-12 object-contain" />
              <span className="text-xs text-white/50 mt-1">A Hope3 Labs service</span>
            </Link>

            {/* Desktop User Menu */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Theme Toggle */}
              <ThemeToggle />
              
              {isSignedIn ? (
                <div className="flex items-center space-x-3">
                  <div className={`text-sm ${classNames.textSecondary}`}>
                    {user?.firstName || user?.emailAddresses[0]?.emailAddress}
                  </div>
                  {isConnected && (
                    <div className={`text-sm ${classNames.textAccent} ${theme === 'dark' ? 'bg-primary-700' : 'bg-gray-100'} px-3 py-1 rounded-full`}>
                      {address?.slice(0, 6)}...{address?.slice(-4)}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  {isConnected ? (
                    <div className={`text-sm ${classNames.textAccent} ${theme === 'dark' ? 'bg-primary-700' : 'bg-gray-100'} px-3 py-1 rounded-full`}>
                      {address?.slice(0, 6)}...{address?.slice(-4)}
                    </div>
                  ) : (
                    <button
                      onClick={connectWallet}
                      className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-primary-700 hover:bg-primary-600' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                    >
                      <Wallet className="w-5 h-5 text-accent-wood" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-2">
              <ThemeToggle />
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 ${classNames.textSecondary} hover:text-accent-wood transition-colors`}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Navigation Links - Centered */}
          <div className="hidden md:flex items-center justify-center space-x-2 pb-4">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={navLinkClass(link.path)}
                >
                  <Icon className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
                  <span>{link.label}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className={`md:hidden ${theme === 'dark' ? 'bg-primary-900/95' : 'bg-white/95'} backdrop-blur-md border-t ${theme === 'dark' ? 'border-primary-700' : 'border-gray-200'}`}>
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={navLinkClass(link.path)}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{link.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content with top padding for sticky nav */}
      <main className={`pt-32 md:pt-40 ${classNames.textPrimary}`}>
        {children}
      </main>
      
      {/* Footer */}
      <footer className="bg-primary-900/50 border-t border-primary-800/50 py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-white/60 mb-4 md:mb-0">
              © 2024 LegacyLayer. All rights reserved.
            </div>
            <div className="text-sm text-white/60">
              Powered by{' '}
              <a 
                href="https://hope3.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-accent-wood hover:text-accent-wood/80 transition-colors font-medium"
              >
                hope3.io
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout; 